/**
 * Modal.tsx - Base modal component with Skinnr theming
 * 
 * CHANGE HISTORY:
 * - 251025_1600 [pending] danielbeer: Initial creation with Skinnr integration
 * 
 * Reusable modal dialog component
 */

import React, { useEffect, useRef } from 'react';
import { PromptType } from '../types';
import './Modal.css';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  type?: PromptType;
  size?: 'small' | 'medium' | 'large';
  closable?: boolean;
  footer?: React.ReactNode;
}

/**
 * Modal - Base modal dialog component
 * 
 * Features:
 * - Skinnr-themed styling
 * - Backdrop click to close
 * - ESC key to close
 * - Focus trap
 * - Accessible (ARIA)
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  type = 'info',
  size = 'medium',
  closable = true,
  footer
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closable) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closable, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Store current focus
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Focus modal
      modalRef.current?.focus();
    } else {
      // Restore focus
      previousFocusRef.current?.focus();
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closable) {
      onClose();
    }
  };

  return (
    <div 
      className="promptr-modal-backdrop" 
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={modalRef}
        className={`promptr-modal promptr-modal-${size} promptr-modal-${type}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="promptr-modal-header">
          <h2 id="modal-title" className="promptr-modal-title">
            {title}
          </h2>
          {closable && (
            <button
              className="promptr-modal-close btn-ghost"
              onClick={onClose}
              aria-label="Close dialog"
            >
              ×
            </button>
          )}
        </div>

        {/* Body */}
        <div className="promptr-modal-body">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="promptr-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
